/* eslint-disable */
import React, { useEffect, useState } from "react";
import { debounce } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import ReactPaginate from "react-paginate";
import { useHistory } from "react-router-dom";
import { Search, FileText, ExternalLink } from "lucide-react";
import RenderSupportPage from "../RenderSupportPage";
import PageSkeleton from "../../Components/PageSkeleton";
import LoanActivityTrail from "../components/LoanActivityTrail";
import { dataActionCreators } from "../../services/Actions";
import { GhanaMoneyDisplay } from "../../helpers/helper";

// Read-only loan lookup for the support desk — the same view /loans/status
// gives officers, including the full activity trail (who booked, who
// authorized, who rejected and the rejection reason), so an agent can answer
// "why was my loan rejected?" without leaving the module or holding the loans
// role. No approve/reject actions here on purpose: this desk answers
// questions, it does not action loans.

const STATUS_STYLES = {
  submitted: "bg-blue-50 dark:bg-blue-900/25 text-blue-700 dark:text-blue-300 border-blue-100 dark:border-blue-800",
  booked: "bg-green-50 dark:bg-green-900/25 text-green-700 dark:text-green-300 border-green-100 dark:border-green-800",
  authorized: "bg-teal-50 dark:bg-teal-900/25 text-teal-700 dark:text-teal-300 border-teal-100 dark:border-teal-800",
  initiated: "bg-amber-50 dark:bg-amber-900/25 text-amber-700 dark:text-amber-300 border-amber-100 dark:border-amber-800",
  paid: "bg-emerald-50 dark:bg-emerald-900/25 text-emerald-700 dark:text-emerald-300 border-emerald-100 dark:border-emerald-800",
  rejected: "bg-red-50 dark:bg-red-900/25 text-red-700 dark:text-red-300 border-red-100 dark:border-red-800",
};

// `status` is a path-style filter on /loans/applied-loans; "all" is the
// unfiltered view the loans module uses.
const STATUS_FILTERS = [
  "all",
  "submitted",
  "booked",
  "authorized",
  "initiated",
  "paid",
  "rejected",
];

function LoanStatusLookup() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { GetAppliedLoans } = bindActionCreators(dataActionCreators, dispatch);

  const data = useSelector((state) => state?.data);
  const { appliedLoans, paginate, isLoading } = data;

  const [search, setSearch] = useState(null);
  const [status, setStatus] = useState("all");
  const [limit, setLimit] = useState(30);
  const [pageCount, setPageCount] = useState(0);
  const [hasLoaded, setHasLoaded] = useState(false);

  const searchQuery = () => (search ? `&search=${search}` : "");

  useEffect(() => {
    Promise.resolve(
      GetAppliedLoans(true, 1, limit, status, searchQuery())
    ).finally(() => setHasLoaded(true));
  }, [limit, status]);

  useEffect(() => {
    setPageCount(Number(paginate?.total) || 0);
  }, [paginate]);

  const handlePageClick = (event) => {
    GetAppliedLoans(true, event.selected + 1, limit, status, searchQuery());
  };

  const onSearchTextChange = debounce((e) => {
    const value = e?.target?.value;
    setSearch(value || null);
    GetAppliedLoans(true, 1, limit, status, value ? `&search=${value}` : "");
  }, 800);

  const rows = appliedLoans || [];

  return (
    <RenderSupportPage title="Loan Status">
      <p className="text-sm text-slate-500 dark:text-slate-400 -mt-4 mb-5">
        Any member's loan applications with the full activity trail — who acted
        at each stage and why a loan was rejected.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px] max-w-sm">
          <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
          <input
            type="search"
            onChange={onSearchTextChange}
            placeholder="Search by staff ID or name"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-slate-700 dark:text-slate-200 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        <select
          value={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
          className="px-3 py-2 text-sm border border-slate-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-slate-700 dark:text-slate-200"
        >
          {[30, 50, 100, 250].map((n) => (
            <option key={n} value={n}>{n} per page</option>
          ))}
        </select>
      </div>

      {/* Status filter */}
      <div className="flex flex-wrap gap-1.5 mb-5">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold capitalize transition-all ${status === s
              ? "bg-indigo-600 text-white shadow-sm"
              : "bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-700"
              }`}
          >
            {s === "all" ? "All" : s}
          </button>
        ))}
      </div>

      {!hasLoaded && rows.length === 0 ? (
        <PageSkeleton cards={0} rows={6} cols={4} />
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead className="bg-slate-50 dark:bg-gray-900/50 border-b border-slate-200 dark:border-gray-700">
                <tr>
                  {["Member Info", "Loan Amount", "Activity Trail", "Status", ""].map((h, i) => (
                    <th key={i} className="px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400  whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-16 text-center">
                      <FileText size={30} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                      <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                        No {status !== "all" ? status : ""} loan applications found
                      </p>
                    </td>
                  </tr>
                ) : (
                  rows.map((loan) => (
                    <tr key={loan?._id} className="hover:bg-slate-50/80 dark:hover:bg-gray-700/40 transition-colors align-top">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <img
                            className="w-10 h-10 rounded-full border border-slate-200 dark:border-gray-600 object-cover shadow-sm"
                            src={loan?.user?.profile_image || "/images/dev/avatar1.png"}
                            alt="Profile"
                          />
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-slate-800 dark:text-slate-100 leading-tight truncate">
                              {loan?.user?.name || loan?.fullname || "—"}
                            </p>
                            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                              {loan?.staff_id || "—"}
                            </p>
                            {loan?.purposeForLoan && (
                              <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate max-w-[180px] mt-0.5">
                                {loan.purposeForLoan}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-100 dark:border-emerald-800">
                          <GhanaMoneyDisplay amount={loan?.loanAmount ?? loan?.initialAmount ?? 0} />
                        </span>
                        <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-1">
                          MD: {loan?.monthlyDeduction ?? "—"}
                          {loan?.loanTerm ? ` · ${loan.loanTerm} mo` : ""}
                        </p>
                      </td>

                      <td className="px-5 py-4 min-w-[280px]">
                        <LoanActivityTrail loan={loan} />
                      </td>

                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border capitalize ${STATUS_STYLES[loan?.status] ||
                          "bg-slate-50 dark:bg-gray-700 text-slate-700 dark:text-slate-300 border-slate-100 dark:border-gray-600"
                          }`}>
                          {loan?.status || "—"}
                        </span>
                        {loan?.officerRejected && (
                          <p className="text-[11px] font-bold text-red-600 dark:text-red-400 mt-1.5">
                            Officer rejected
                          </p>
                        )}
                      </td>

                      <td className="px-5 py-4 text-right">
                        {loan?.staff_id && (
                          <button
                            title="Open member profile"
                            onClick={() =>
                              history.push(`/support/member-detailed?staff_id=${loan.staff_id}`)
                            }
                            className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-gray-700 rounded-lg transition-all"
                          >
                            <ExternalLink size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pageCount > 1 && (
            <div className="border-t border-slate-100 dark:border-gray-700">
              <ReactPaginate
                breakLabel="..."
                nextLabel=">"
                previousLabel="<"
                onPageChange={handlePageClick}
                pageRangeDisplayed={5}
                pageCount={pageCount}
                renderOnZeroPageCount={null}
                containerClassName="flex items-center justify-center gap-1 py-4"
                pageLinkClassName="px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-gray-700 hover:text-indigo-600 transition-colors"
                previousLinkClassName="px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-gray-700 transition-colors"
                nextLinkClassName="px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-gray-700 transition-colors"
                activeLinkClassName="!bg-indigo-600 !text-white shadow-sm"
              />
            </div>
          )}
        </div>
      )}
    </RenderSupportPage>
  );
}

export default LoanStatusLookup;
